// Route-level boundary for the customer review screens.
//
// Before W1B this component kept a hard-coded SARAH id allowlist and
// rendered a static "not available" fallback for everything else. That
// shipped as an in-code reminder that a route id is not a customer data
// source, but it was explicitly NOT production authorization: the only
// customer that resolved was the Sarah fixture.
//
// W1B replaces that allowlist with a scoped loader (useAuthenticatedCustomer):
//
//   - production: a signed GET /v1/contacts/:id against the api-server
//   - preview: ?preview=sarah OR <PreviewFixtureProvider /> loads the
//     SARAH fixture synchronously for dev / design review
//   - tests: a <CustomerLoaderProvider /> injects a sync hook
//
// The boundary renders five explicit states (loading, ready, unauthorized,
// not-found, error) and never falls back to fixture data in production.
// History and Letters still have their own source guards; this boundary
// stops at the fixture review group.

import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { getReviewReturnPath } from './ReviewState.js';
import {
  useAuthenticatedCustomer,
  type AuthorizedCustomer,
  type CustomerLoadResult,
} from './useAuthenticatedCustomer.js';
import { WorkspaceChrome } from './WorkspaceChrome.js';

export function CustomerReviewBoundary(): ReactNode {
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuthenticatedCustomer(customerId ?? null);

  if (auth.status === 'ready') return <Outlet />;
  if (auth.status === 'loading') return <LoadingCustomer />;

  const returnTarget = resolveReturnTarget(location.state, customerId ?? '');
  const onReturn = () => navigate(returnTarget.path);

  if (auth.status === 'unauthorized') {
    return <UnauthorizedCustomer returnLabel={returnTarget.label} onReturn={onReturn} />;
  }
  if (auth.status === 'error') {
    return <CustomerLoadError error={auth.error} returnLabel={returnTarget.label} onReturn={onReturn} />;
  }
  // 'not-found': keep the pre-R1 heading so screens linking into this
  // boundary from LetterReady and other fixtures still match the text.
  return <NotFoundCustomer returnLabel={returnTarget.label} onReturn={onReturn} />;
}

export interface ReturnTarget {
  path: string;
  label: string;
}

export function resolveReturnTarget(
  state: unknown,
  customerId: string,
): ReturnTarget {
  const candidate = getReviewReturnPath(state, customerId, '/');
  const pathname = candidate.split(/[?#]/, 1)[0];
  const unavailableFixtureRoute =
    /^\/(?:current-action|build-order|payment|subscriptions|colic-coverage|gifts|horses|relationships)\/[^/]+/.test(
      pathname,
    );
  const safe =
    unavailableFixtureRoute
      ? '/'
      : ['/my-work', '/my-activity', '/letters', '/'].includes(pathname) ||
          /^\/(?:history|letters)\/[^/]+$/.test(pathname)
        ? candidate
        : '/';
  const label = safe.startsWith('/letters')
    ? 'Return to assigned letters'
    : safe.startsWith('/history/')
      ? 'Return to customer history'
      : safe.startsWith('/my-activity')
        ? 'Return to My Activity'
        : safe === '/my-work'
          ? 'Return to My work'
          : 'Back to queue';
  return { path: safe, label };
}

// --- State renderers ----------------------------------------------------

function LoadingCustomer(): ReactNode {
  return (
    <WorkspaceChrome rail="my-work">
      <section
        className="card"
        style={{ padding: 24 }}
        aria-labelledby="customer-review-loading"
        aria-busy="true"
      >
        <span className="eyebrow">LOADING CUSTOMER</span>
        <h1 id="customer-review-loading">Loading customer…</h1>
        <p role="status">Fetching the authorized customer record.</p>
      </section>
    </WorkspaceChrome>
  );
}

function UnauthorizedCustomer({
  returnLabel,
  onReturn,
}: {
  returnLabel: string;
  onReturn: () => void;
}): ReactNode {
  return (
    <WorkspaceChrome rail="my-work">
      <section
        className="card"
        style={{ padding: 24 }}
        aria-labelledby="customer-review-unauthorized"
      >
        <span className="eyebrow">ACCESS DENIED</span>
        <h1 id="customer-review-unauthorized">
          You don’t have access to this customer.
        </h1>
        <p>
          This customer is not in your assigned scope. Return to the queue to
          pick up your next task.
        </p>
        <button type="button" className="btn" onClick={onReturn}>
          {returnLabel}
        </button>
      </section>
    </WorkspaceChrome>
  );
}

function CustomerLoadError({
  error,
  returnLabel,
  onReturn,
}: {
  error: Error;
  returnLabel: string;
  onReturn: () => void;
}): ReactNode {
  return (
    <WorkspaceChrome rail="my-work">
      <section
        className="card"
        style={{ padding: 24 }}
        aria-labelledby="customer-review-error"
      >
        <span className="eyebrow">COULD NOT LOAD CUSTOMER</span>
        <h1 id="customer-review-error">We could not load this customer.</h1>
        <p role="alert">{error.message}</p>
        <button type="button" className="btn" onClick={onReturn}>
          {returnLabel}
        </button>
      </section>
    </WorkspaceChrome>
  );
}

function NotFoundCustomer({
  returnLabel,
  onReturn,
}: {
  returnLabel: string;
  onReturn: () => void;
}): ReactNode {
  return (
    <WorkspaceChrome rail="my-work">
      <section
        className="card"
        style={{ padding: 24 }}
        aria-labelledby="customer-review-unavailable"
      >
        <span className="eyebrow">CUSTOMER NOT LOADED</span>
        <h1 id="customer-review-unavailable">
          This customer’s review is not available.
        </h1>
        <p>
          Details for the selected customer have not been loaded. A route id
          by itself is not a customer source, and the review screens never
          fall back to sample data under an arbitrary customer id.
        </p>
        <button type="button" className="btn" onClick={onReturn}>
          {returnLabel}
        </button>
      </section>
    </WorkspaceChrome>
  );
}

// Test-only exports.
export const __test__ = {
  resolveReturnTarget,
};

export type { AuthorizedCustomer, CustomerLoadResult };
